import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSonner } from 'sonner';

/**
 * Reserved space for the toasts, so they never sit on top of a button.
 *
 * The <Toaster> (App.tsx) floats just below the sticky header. No corner is free on every screen — the top band
 * holds page actions, the bottom holds the driver's Delivered / Failed / Skip rows and the mobile Home bar — so
 * instead of an overlay we reserve the space: while toasts show, the measured bottom edge of the stack is published
 * as `--toast-reserve` on <html>. This spacer (right under the header, sticky with it) pushes the page down by that
 * much, and DialogContent shifts open dialogs down the same way.
 *
 * The reserve only grows while toasts are on screen and is released on the next scroll, route change or resize —
 * never on its own timer, so the page does not jump back up under a finger that is about to tap.
 */

const HEADER_PX = 56; // AppShell header, h-14
const GAP_PX = 8;

function measureStackBottom(): number {
  const toaster = document.querySelector('[data-sonner-toaster]');
  // A hovered/tapped stack fans out; reserving for that would shove the page around under the pointer.
  if (!toaster || toaster.querySelector('[data-sonner-toast][data-expanded="true"]')) return -1;
  let bottom = 0;
  toaster.querySelectorAll<HTMLElement>('[data-sonner-toast][data-visible="true"]:not([data-removed="true"])').forEach((el) => {
    bottom = Math.max(bottom, el.getBoundingClientRect().bottom);
  });
  return bottom > 0 ? Math.ceil(bottom + GAP_PX) : 0;
}

function setReserve(px: number) {
  const root = document.documentElement;
  // Keep what the user was looking at under the same pixels: without this, scroll anchoring would scroll the page
  // to compensate for the spacer and the toast would cover content again.
  const y = window.scrollY;
  if (px > 0) root.style.setProperty('--toast-reserve', `${px}px`);
  else root.style.removeProperty('--toast-reserve');
  if (window.scrollY !== y) window.scrollTo(window.scrollX, y);
}

export function useToastReserve(): number {
  const { toasts } = useSonner();
  const [reserve, setReserveState] = useState(0);
  const active = toasts.length > 0;

  // While toasts exist, follow the stack's enter animation and grow-only.
  useEffect(() => {
    if (!active) return;
    let frame = 0;
    const tick = () => {
      const bottom = measureStackBottom();
      if (bottom > 0) setReserveState((current) => Math.max(current, bottom));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active]);

  // Once the last toast is gone, give the space back at the next moment the page is moving anyway.
  const { pathname } = useLocation();
  useEffect(() => {
    if (active || reserve === 0) return;
    const release = () => setReserveState(0);
    window.addEventListener('scroll', release, { passive: true, once: true });
    window.addEventListener('resize', release, { once: true });
    return () => {
      window.removeEventListener('scroll', release);
      window.removeEventListener('resize', release);
    };
  }, [active, reserve]);
  useEffect(() => {
    setReserveState(0);
  }, [pathname]);

  useLayoutEffect(() => {
    setReserve(reserve);
  }, [reserve]);
  useEffect(() => () => setReserve(0), []);

  return reserve;
}

export function ToastDock() {
  const reserve = useToastReserve();
  return (
    <div
      aria-hidden
      data-toast-dock
      className="sticky top-14 z-20 shrink-0 bg-background"
      style={{ height: Math.max(0, reserve - HEADER_PX) }}
    />
  );
}
